import React from 'react';
import { useNavigate } from 'react-router-dom';

function PricingPlans({ onSelectPlan, onUploadClick }) {
  const navigate = useNavigate();
  const plans = [
    {
      name: 'Free',
      pageLimit: 'Up to 3 pages',
      description: 'Full summary and risk score for up to 3 pages—no payment required.',
      price: '$0',
      features: [
        'Full summary (up to 3 pages)',
        'Risk score (up to 3 pages)',
        'No payment required',
      ]
    },
    {
      name: 'Quick Check',
      pageLimit: 'Per 20 pages',
      description: 'Full summary and risk analysis',
      price: '$9',
      features: [
        'Full summary',
        'Comprehensive risk analysis'
      ]
    }
  ];

  const handlePlanSelect = (plan) => {
    if (plan.name === 'Free') {
      navigate('/upload/free');
    } else if (plan.name === 'Quick Check') {
      navigate('/upload/quick_check');
    } else if (onSelectPlan) {
      onSelectPlan(plan);
    } else {
      // Default behavior: show alert and suggest uploading
      if (onUploadClick) {
        onUploadClick();
      }
    }
  };

  return (
    <div className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600">
            Pay once, get your analysis. No subscriptions, no hidden fees.
          </p>
        </div>

        {/* Main plans row */}
        <div className="flex flex-wrap justify-center gap-8 mb-12">
          {plans.map((plan, index) => (
            <div key={plan.name} className="relative flex flex-col items-center w-full md:w-1/3 max-w-md">
              {/* Most Popular Badge (optional for Quick Check) */}
              {plan.name === 'Quick Check' && (
                <div className="absolute -top-7 left-1/2 transform -translate-x-1/2 z-10">
                  <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium shadow-lg">
                    Most Popular
                  </span>
                </div>
              )}
              <div
                className={`bg-white rounded-lg shadow-lg border-2 flex flex-col flex-1 w-full p-8 pt-12 transition-transform duration-200 ${
                  plan.name === 'Quick Check' ? 'border-blue-500 scale-105 z-0' : 'border-gray-200'
                }`}
                style={{ minHeight: 400 }}
              >
                <div className="text-center flex-1 flex flex-col">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-2">
                    {plan.name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-1">{plan.pageLimit}</p>
                  <p className="text-xs text-gray-500 mb-4">{plan.description}</p>
                  <div className="mb-6 flex flex-col items-center">
                    <span className="text-3xl font-bold text-gray-900">{plan.price}</span>
                  </div>
                  <ul className="text-left space-y-3 mb-6">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto">
                    <button 
                      onClick={() => handlePlanSelect(plan)}
                      className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                        plan.name === 'Quick Check'
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                      }`}>
                      Choose {plan.name}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 p-6 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
            How the Freemium Model Works
          </h3>
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-blue-600 font-bold">1</span>
              </div>
              <h4 className="font-medium text-gray-900 mb-2">Upload for Free</h4>
              <p className="text-sm text-gray-600">
                Upload any document and get instant risk score + partial summary
              </p>
            </div>
            <div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-blue-600 font-bold">2</span>
              </div>
              <h4 className="font-medium text-gray-900 mb-2">Preview Results</h4>
              <p className="text-sm text-gray-600">
                See key risks and a sample of the full analysis
              </p>
            </div>
            <div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-blue-600 font-bold">3</span>
              </div>
              <h4 className="font-medium text-gray-900 mb-2">Pay to Unlock</h4>
              <p className="text-sm text-gray-600">
                One-time payment reveals the complete analysis
              </p>
            </div>
          </div>
        </div>

        {onUploadClick && (
          <div className="text-center mt-8">
            <button 
              onClick={() => navigate('/upload/free')}
              className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Upload Your Document Now
            </button>
            <p className="text-sm text-gray-600 mt-2">
              Start with a free preview - no payment required
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default PricingPlans; 